/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { TournamentClient, PersonClient, Tournament, Person } from '../../utils/apiClient';
import './AdminDashboard.scss';
import { handleError } from '../../utils';
import AppContext from '../../AppContext';

const AdminDashboard = () => {
  const appContext = React.useContext(AppContext);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tournaments');
  const navigate = useNavigate();

  const tournamentClient = new TournamentClient(ApiUrl, axios);
  const personClient = new PersonClient(ApiUrl, axios);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Загрузка турниров
        const tournamentsData = await tournamentClient.getTournaments();
        setTournaments(tournamentsData);

        // Загрузка персон
        const personsData = await personClient.getPersons();
        setPersons(personsData);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteTournament = async (id: number) => {
    if (!confirm('Вы уверены, что хотите удалить этот турнир?')) {
      return;
    }

    try {
      await tournamentClient.deleteTournament(id);
      // Обновление списка турниров
      const updatedTournaments = tournaments.filter(t => t.id !== id);
      setTournaments(updatedTournaments);
    } catch (error) {
      console.error('Ошибка удаления турнира:', error);
      alert('Ошибка при удалении турнира. Возможно, турнир уже начат.');
    }
  };

  const handleDeletePerson = async (id: number) => {
    if (!confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      await personClient.deletePerson(id);
      // Обновление списка персон
      const updatedPersons = persons.filter(p => p.id !== id);
      setPersons(updatedPersons);
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      alert('Ошибка при удалении пользователя. Возможно, у него есть связанные данные.');
    }
  };

  const renderTournaments = () => {
    return (
      <div className="tournaments-section">
        <div className="admin-dashboard__section-header">
          <h3 className="section-heading">Управление турнирами</h3>
          <button
            onClick={() => navigate('/tournaments/create')}
            className="btn admin-dashboard__create-btn"
          >
            Создать турнир
          </button>
        </div>

        <div className="admin-dashboard__card-container">
          {tournaments.length === 0 ? (
            <p>Турниры не найдены</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Место проведения</th>
                  <th>Даты</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tournaments.map(tournament => (
                  <tr key={tournament.id}>
                    <td>{tournament.name}</td>
                    <td>{tournament.location}</td>
                    <td>
                      {new Date(tournament.startDate).toLocaleDateString()} -
                      {new Date(tournament.endDate).toLocaleDateString()}
                    </td>
                    <td>
                      {tournament.isFinished ? 'Завершен' :
                        tournament.isStarted ? 'Активен' : 'Предстоящий'}
                    </td>
                    <td className="admin-dashboard__actions">
                      <button
                        onClick={() => navigate(`/tournaments/${tournament.id}`)}
                        className="btn btn--view"
                      >
                        Просмотр
                      </button>
                      {!tournament.isStarted && (
                        <>
                          <button
                            onClick={() => navigate(`/tournaments/${tournament.id}/edit`)}
                            className="btn btn--edit"
                          >
                            Редактировать
                          </button>
                          <button
                            onClick={() => handleDeleteTournament(tournament.id!)}
                            className="btn btn--delete"
                          >
                            Удалить
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderPersons = () => {
    return (
      <div className="persons-section">
        <div className="admin-dashboard__section-header">
          <h3 className="section-heading">Управление пользователями</h3>
          <button
            onClick={() => navigate('/register')}
            className="btn admin-dashboard__create-btn"
          >
            Создать пользователя
          </button>
        </div>

        <div className="admin-dashboard__card-container">
          {persons.length === 0 ? (
            <p>Пользователи не найдены</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Дата рождения</th>
                  <th>Город</th>
                  <th>Роли</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {persons.map(person => (
                  <tr key={person.id}>
                    <td>{person.lastName} {person.firstName} {person.patronymic || ''}</td>
                    <td>{new Date(person.birthDate).toLocaleDateString()}</td>
                    <td>{person.city}</td>
                    <td>
                      {person.personRoles && person.personRoles.map(role =>
                        role.role?.name
                      ).join(', ')}
                    </td>
                    <td className="admin-dashboard__actions">
                      <button
                        onClick={() => navigate(`/persons/${person.id}`)}
                        className="btn btn--view"
                      >
                        Просмотр
                      </button>
                      <button
                        onClick={() => navigate(`/persons/${person.id}/edit`)}
                        className="btn btn--edit"
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeletePerson(person.id!)}
                        className="btn btn--delete"
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div>Загрузка панели администратора...</div>;
  }

  return (
    <div className="admin-dashboard">
      <h2 className="page-heading">Панель администратора</h2>

      <div className="admin-dashboard__tabs">
        <button
          className={activeTab === 'tournaments' ? 'admin-dashboard__tab admin-dashboard__tab--active' : 'admin-dashboard__tab'}
          onClick={() => setActiveTab('tournaments')}
        >
          Турниры
        </button>
        <button
          className={activeTab === 'persons' ? 'admin-dashboard__tab admin-dashboard__tab--active' : 'admin-dashboard__tab'}
          onClick={() => setActiveTab('persons')}
        >
          Пользователи
        </button>
      </div>

      <div className="admin-dashboard__content">
        {activeTab === 'tournaments' ? renderTournaments() : renderPersons()}
      </div>
    </div>
  );
};

export default AdminDashboard;
